import { dayKeyInZone } from '@/lib/dayKey';
import { isWinnerlessRow } from '@/draw/core/matchOutcome';
import type { PublicMatch } from './scheduleStatus';
import type { SlotRow } from './slotGrid';

/** Where the NOW line goes: inside a row, `fraction` of the way down it, or a row of its own at an edge. */
export type NowPlacement = { row: number; fraction: number } | { edge: 'before' | 'after' };

const MIN = 60_000;

/**
 * Where the NOW line sits, read as real time (Courtside's rule): inside the row now falls in, as far
 * down as now is between that row's start and the next row's start, so at 10:07 a 10:00 row is
 * 7/30 through and the line reads 10:07, never the next row's time. The last row ends when its
 * longest planned match is due to end; past that (or before the first start) the line is an edge row.
 * Null off today: `scope.day` is the calendar day shown, read in `scope.zone`.
 */
export function nowPlacement(
  rows: Pick<SlotRow, 'startIso' | 'cells'>[],
  nowMs: number,
  scope: { day: string; zone: string },
): NowPlacement | null {
  if (rows.length === 0) return null;
  if (dayKeyInZone(new Date(nowMs).toISOString(), scope.zone) !== scope.day) return null;
  const starts = rows.map((r) => Date.parse(r.startIso));
  if (nowMs < starts[0]) return { edge: 'before' };
  let i = 0;
  while (i < rows.length - 1 && starts[i + 1] <= nowMs) i += 1;
  let end: number;
  if (i < rows.length - 1) {
    end = starts[i + 1];
  } else {
    const longest = Math.max(0, ...rows[i].cells.flat().map((m) => m.durationEstimateMin));
    end = starts[i] + longest * MIN;
  }
  if (nowMs >= end) return { edge: 'after' }; // only the last row can end before now
  return { row: i, fraction: (nowMs - starts[i]) / (end - starts[i]) };
}

/** CSS `top` for the line: clear of the row's own time label above and of the next row below. */
export function nowLineTop(fraction: number): string {
  return `clamp(4rem, ${(fraction * 100).toFixed(3)}%, calc(100% - 0.75rem))`;
}

/**
 * Which side (index into `players`) won a finished match, or null. Only when the data names one:
 * a winnerless outcome (dual withdrawal, not played) or a level/missing score has no winner.
 */
export function winnerSide(m: PublicMatch): 0 | 1 | null {
  if (m.status !== 'completed' || !m.score) return null;
  if (isWinnerlessRow({ outcome: m.outcome, is_walkover: m.isWalkover })) return null;
  // A retirement's winner is winner_id (the retiree can lead in games), never the games leader.
  if (m.outcome === 'retired' || m.retiredPlayerId) {
    const index = m.winnerId ? (m.playerIds ?? []).indexOf(m.winnerId) : -1;
    return index === 0 || index === 1 ? index : null;
  }
  if (m.score.p1 === m.score.p2) return null;
  return m.score.p1 > m.score.p2 ? 0 : 1;
}
